/**
 * Steps 3 (access configuration) and 4 (review & send) of the Add User wizard.
 */
import type { Company, Role, UserSitePermissionFlags } from '../../lib/api';
import { departmentLabel } from '../../lib/departments';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { PermissionMatrix, PermissionSummary, ReviewBlock } from './PermissionMatrix';
import type { PermissionFlags, ProfileFields } from './constants';

interface AccessStepProps {
  applyToAll: boolean;
  onApplyToAllChange: (value: boolean) => void;
  defaultFlags: PermissionFlags;
  selectedSiteIds: number[];
  primaryCompanyId: number;
  companies: Company[];
  flagsForSite: (siteId: number) => PermissionFlags;
  /** `siteId` null edits the shared defaults. */
  setFlag: (siteId: number | null, key: keyof UserSitePermissionFlags, value: boolean) => void;
  /** False on supplier invites: the Documents switch only reaches customer users. */
  showDocuments: boolean;
}

export function AccessStep({
  applyToAll, onApplyToAllChange, defaultFlags, selectedSiteIds,
  primaryCompanyId, companies, flagsForSite, setFlag, showDocuments,
}: AccessStepProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>3. Access configuration</CardTitle>
        <CardDescription>
          Modules, notifications, and the read-only override.
          Apply the same settings to every site, or override per site.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between border rounded-md p-3 bg-muted/30">
          <div>
            <p className="font-medium text-sm">Apply same settings to every site</p>
            <p className="text-xs text-muted-foreground">
              Turn off to fine-tune access per site below.
            </p>
          </div>
          <Switch checked={applyToAll} onCheckedChange={onApplyToAllChange}
            aria-label="Apply same settings to every site" />
        </div>

        {applyToAll ? (
          <PermissionMatrix
            title="Default access (applies to every selected site)"
            flags={defaultFlags}
            onChange={(key, value) => setFlag(null, key, value)}
            showDocuments={showDocuments}
          />
        ) : (
          <div className="space-y-3">
            {selectedSiteIds.map(sid => {
              const site = companies.find(c => c.id === sid);
              const isPrimary = sid === primaryCompanyId;
              return (
                <PermissionMatrix
                  key={sid}
                  title={site?.name || `Site #${sid}`}
                  subtitle={isPrimary ? 'Primary company' : undefined}
                  flags={flagsForSite(sid)}
                  onChange={(key, value) => setFlag(sid, key, value)}
                  showDocuments={showDocuments}
                />
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface ReviewStepProps {
  profile: ProfileFields;
  primaryCompany: Company | undefined;
  additionalSiteIds: Set<number>;
  companies: Company[];
  roles: Role[];
  applyToAll: boolean;
  defaultFlags: PermissionFlags;
  selectedSiteIds: number[];
  flagsForSite: (siteId: number) => PermissionFlags;
  showDocuments: boolean;
}

export function ReviewStep({
  profile, primaryCompany, additionalSiteIds, companies, roles,
  applyToAll, defaultFlags, selectedSiteIds, flagsForSite, showDocuments,
}: ReviewStepProps) {
  const department = departmentLabel(profile.department);
  return (
    <Card>
      <CardHeader>
        <CardTitle>4. Review &amp; send</CardTitle>
        <CardDescription>Sanity-check before the invitation email goes out.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <ReviewBlock label="Profile">
          <div>{profile.salutation && <span className="text-muted-foreground mr-1">{profile.salutation}</span>}<strong>{profile.first_name} {profile.last_name}</strong></div>
          <div className="text-muted-foreground">{profile.email}{profile.phone ? ` · ${profile.phone}` : ''}</div>
          {(profile.job_title || department) && (
            <div className="text-xs text-muted-foreground mt-1">
              {profile.job_title}{profile.job_title && department ? ' · ' : ''}{department}
            </div>
          )}
        </ReviewBlock>

        <ReviewBlock label="Companies">
          <div><strong>Primary:</strong> {primaryCompany?.name || '—'}</div>
          {additionalSiteIds.size > 0 && (
            <div className="mt-1">
              <strong>Additional sites:</strong>
              <ul className="list-disc list-inside text-muted-foreground">
                {Array.from(additionalSiteIds).map(sid => (
                  <li key={sid}>{companies.find(c => c.id === sid)?.name || `Site #${sid}`}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-2">
            User Role: {roles.find(r => r.id === profile.role)?.name || '—'}
          </div>
        </ReviewBlock>

        <ReviewBlock label="Access">
          <div className="text-xs text-muted-foreground mb-1">
            {applyToAll ? 'Same settings applied to every site' : 'Per-site overrides'}:
          </div>
          {applyToAll ? (
            <PermissionSummary flags={defaultFlags} showDocuments={showDocuments} />
          ) : (
            <div className="space-y-2">
              {selectedSiteIds.map(sid => (
                <div key={sid} className="border-l-2 border-primary/40 pl-2">
                  <div className="font-medium">{companies.find(c => c.id === sid)?.name || `Site #${sid}`}</div>
                  <PermissionSummary flags={flagsForSite(sid)} showDocuments={showDocuments} />
                </div>
              ))}
            </div>
          )}
        </ReviewBlock>

        {profile.message && (
          <ReviewBlock label="Welcome message">
            <p className="whitespace-pre-wrap text-muted-foreground italic">{profile.message}</p>
          </ReviewBlock>
        )}
      </CardContent>
    </Card>
  );
}
